/**
 * HR Signup Page with Duration Selection and Payment Integration
 * Flow: Select duration → Fill signup form → Make payment → Create account with Core subscription
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, Mail, Lock, Phone, Building2, Globe, CreditCard, Loader2, CheckCircle, ChevronRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { handleRazorpayPayment, getPlanDetails } from '../../services/razorpayService';
import type { RazorpaySuccessResponse } from '../../types/razorpay.types';
import toast from 'react-hot-toast';

type PlanDuration = '1 month' | '3 months' | '6 months' | '12 months';

const HRSignup: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();

  const [currentStep, setCurrentStep] = useState<'duration' | 'form' | 'payment' | 'processing' | 'success'>('duration');
  const [selectedDuration, setSelectedDuration] = useState<PlanDuration>('1 month');

  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    companyWebsite: '',
  });

  const [paymentData, setPaymentData] = useState<RazorpaySuccessResponse | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const durations: PlanDuration[] = ['1 month', '3 months', '6 months', '12 months'];

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';

    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    else if (!/^\d{10}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Invalid phone number (10 digits required)';
    }

    // Company website is optional
    if (formData.companyWebsite && !/^https?:\/\/.+/.test(formData.companyWebsite)) {
      newErrors.companyWebsite = 'Invalid website URL (should start with http:// or https://)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const handleDurationSelect = (duration: PlanDuration) => {
    setSelectedDuration(duration);
    setCurrentStep('form');
  };

  const calculateExpiryDate = (duration: PlanDuration): Date => {
    const now = new Date();
    switch (duration) {
      case '1 month':
        return new Date(now.setMonth(now.getMonth() + 1));
      case '3 months':
        return new Date(now.setMonth(now.getMonth() + 3));
      case '6 months':
        return new Date(now.setMonth(now.getMonth() + 6));
      case '12 months':
        return new Date(now.setFullYear(now.getFullYear() + 1));
      default:
        return new Date(now.setMonth(now.getMonth() + 1));
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      setCurrentStep('payment');
      initiatePayment();
    }
  };

  const initiatePayment = async () => {
    const plan = getPlanDetails('HR', selectedDuration);

    try {
      await handleRazorpayPayment(
        plan.amount,
        'HR',
        selectedDuration,
        {
          name: formData.companyName || formData.name,
          email: formData.email,
          contact: formData.phone,
        },
        handlePaymentSuccess,
        handlePaymentFailure
      );
    } catch (error: any) {
      toast.error('Failed to open payment gateway');
      setCurrentStep('form');
    }
  };

  const handlePaymentSuccess = async (response: RazorpaySuccessResponse) => {
    setPaymentData(response);
    setCurrentStep('processing');

    const plan = getPlanDetails('HR', selectedDuration);

    try {
      const success = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        company: formData.companyName,
        companyWebsite: formData.companyWebsite,
        userType: 'hr',
        // Subscription data
        isPremium: true,
        subscriptionPlan: 'Core',
        subscriptionDuration: selectedDuration,
        subscriptionAmount: plan.amount,
        subscriptionStartDate: new Date(),
        subscriptionExpiryDate: calculateExpiryDate(selectedDuration),
        paymentId: response.razorpay_payment_id,
        orderId: response.razorpay_order_id,
        paymentSignature: response.razorpay_signature,
      });

      if (success) {
        setCurrentStep('success');
        toast.success('Account created successfully!');

        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        throw new Error('Failed to create account');
      }
    } catch (error: any) {
      toast.error('Payment successful but account creation failed. Please contact support.');
      setCurrentStep('form');
    }
  };

  const handlePaymentFailure = (error: any) => {
    toast.error(error.message || 'Payment failed. Please try again.');
    setCurrentStep('form');
  };

  // Success Screen
  if (currentStep === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 relative overflow-hidden">
        {/* Animated Background */}
        <div className="fixed inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50"></div>
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-blue-400/20 to-purple-600/20 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-purple-400/20 to-pink-600/20 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="max-w-md w-full glass-card backdrop-blur-glass-strong text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <CheckCircle className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Welcome to VetriaAI!</h1>
          <p className="text-lg text-gray-600 mb-4">
            Your Core subscription is now active. Redirecting you to login...
          </p>
          <div className="w-8 h-8 border-3 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }

  // Processing Screen
  if (currentStep === 'processing') {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 relative overflow-hidden">
        {/* Animated Background */}
        <div className="fixed inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50"></div>
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-blue-400/20 to-purple-600/20 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-purple-400/20 to-pink-600/20 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="max-w-md w-full glass-card backdrop-blur-glass-strong text-center">
          <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Creating Your Account</h2>
          <p className="text-gray-600">
            Please wait while we set up your Core plan account...
          </p>
        </div>
      </div>
    );
  }

  // Duration Selection Screen
  if (currentStep === 'duration') {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 relative overflow-hidden">
        {/* Animated Background */}
        <div className="fixed inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50"></div>
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-blue-400/20 to-purple-600/20 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-purple-400/20 to-pink-600/20 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="max-w-4xl w-full">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Join as HR / Recruiter</h1>
            <p className="text-2xl font-semibold text-gradient">Choose your Core Plan duration</p>
          </div>

          <div className="glass-card backdrop-blur-glass-strong">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Select Plan Duration
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {durations.map((duration) => {
                const plan = getPlanDetails('HR', duration);
                const savings = (plan as any).savings;

                return (
                  <button
                    key={duration}
                    onClick={() => handleDurationSelect(duration)}
                    className="relative p-6 rounded-glass transition-all group bg-white/60 backdrop-blur-sm border-2 border-white/40 hover:bg-white/80 hover:border-purple-500/50 hover:shadow-glass"
                  >
                    {savings && (
                      <div className="absolute -top-2 -right-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                        Save ₹{savings}
                      </div>
                    )}
                    <div className="text-left">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{duration}</h3>
                      <div className="flex items-baseline mb-4">
                        <span className="text-3xl font-extrabold text-gradient">
                          ₹{plan.amount.toLocaleString('en-IN')}
                        </span>
                      </div>
                      <div className="flex items-center text-purple-600 font-semibold group-hover:translate-x-2 transition-transform">
                        Select Plan
                        <ChevronRight className="w-5 h-5 ml-1" />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-8 text-center">
              <p className="text-sm text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="font-semibold text-purple-600 hover:text-gradient transition-all">
                  Login here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Form Screen
  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 relative overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50"></div>
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-blue-400/20 to-purple-600/20 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-purple-400/20 to-pink-600/20 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Create Your Account</h1>
          <p className="text-xl font-semibold text-gradient mb-2">
            Core Plan - {selectedDuration} - ₹{getPlanDetails('HR', selectedDuration).amount.toLocaleString('en-IN')}
          </p>
          <button
            onClick={() => setCurrentStep('duration')}
            className="text-sm text-purple-600 hover:text-gradient font-medium transition-all"
          >
            ← Change plan
          </button>
        </div>

        <div className="glass-card backdrop-blur-glass-strong">
          <form onSubmit={handleFormSubmit} className="space-y-5">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="John Doe"
                />
              </div>
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>

            {/* Company Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                    errors.companyName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Acme Inc"
                />
              </div>
              {errors.companyName && <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="john@acme.com"
                />
              </div>
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="9876543210"
                />
              </div>
              {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
            </div>

            {/* Company Website (Optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Website <span className="text-gray-400">(Optional)</span>
              </label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="companyWebsite"
                  value={formData.companyWebsite}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                    errors.companyWebsite ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="https://acme.com"
                />
              </div>
              {errors.companyWebsite && <p className="mt-1 text-sm text-red-600">{errors.companyWebsite}</p>}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Min. 6 characters"
                />
              </div>
              {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                    errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Re-enter password"
                />
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={currentStep === 'payment'}
              className="w-full flex items-center justify-center gap-2 py-3 px-6 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {currentStep === 'payment' ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Opening Payment Gateway...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5" />
                  Proceed to Payment (₹{getPlanDetails('HR', selectedDuration).amount.toLocaleString('en-IN')})
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-indigo-600 hover:text-indigo-700 font-semibold">
                Login here
              </Link>
            </p>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-500">
              🔒 Secure payment powered by Razorpay
              <br />
              Your payment information is encrypted and secure
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HRSignup;
